import React, { Reducer, useContext, useEffect } from "react";
import { createContext, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { Dispatch, ProviderProps } from "../../common/types";
import {
  preferencesReducer,
  IPreferencesState,
  initialPreferencesState,
} from "./reducer";
import {
  PreferencesAction,
  setLanguage as setLanguageAction,
  toggleTheme,
} from "./action";
import {
  LOCAL_STORAGE_KEYS,
  SUPPORTED_LANGUAGES,
} from "../../common/constants";

const PreferencesContext = createContext<
  | { state: IPreferencesState; dispatch: Dispatch<PreferencesAction> }
  | undefined
>(undefined);

export const PreferencesProvider: React.FC<ProviderProps> = (
  props: ProviderProps,
) => {
  const [state, dispatch] = useReducer<
    Reducer<IPreferencesState, PreferencesAction>
  >(preferencesReducer, initialPreferencesState);
  const { i18n } = useTranslation();

  // Initialize language from localStorage or browser preference
  useEffect(() => {
    const storedLanguage = localStorage.getItem(
      LOCAL_STORAGE_KEYS.LANGUAGE,
    ) as SUPPORTED_LANGUAGES;
    const storedTheme = localStorage.getItem(LOCAL_STORAGE_KEYS.THEME);
    const browserLanguage = navigator.language.startsWith("ar")
      ? SUPPORTED_LANGUAGES.AR
      : SUPPORTED_LANGUAGES.EN;

    const initialLanguage = storedLanguage || browserLanguage;

    if (initialLanguage !== initialPreferencesState.currentLanguage) {
      dispatch(setLanguageAction(initialLanguage));
      i18n.changeLanguage(initialLanguage);
    }

    if (storedTheme === "dark" && !initialPreferencesState.isDarkMode) {
      dispatch(toggleTheme());
    }
  }, [i18n]);

  // Apply RTL/LTR direction to document
  useEffect(() => {
    document.documentElement.dir = state.direction;
    document.documentElement.lang = state.currentLanguage;
  }, [state.direction, state.currentLanguage]);

  // Store preferences in localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.LANGUAGE, state.currentLanguage);
  }, [state.currentLanguage]);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.THEME,
      state.isDarkMode ? "dark" : "light",
    );
  }, [state.isDarkMode]);

  const value = { state, dispatch };
  return (
    <PreferencesContext.Provider value={value}>
      {props.children}
    </PreferencesContext.Provider>
  );
};

export function usePreferencesContext() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error(
      "usePreferencesContext must be used within a PreferencesProvider",
    );
  }
  return context;
}
